import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { de, hasAtLeastRole } from '@fsv/core';
import { useInboxBadge, useOptionalTenant, useSession } from '../data/hooks';
import { STORAGE_FULL_EVENT } from '../data/backendFactory';
import { tx } from '@fsv/data';
import { formatTime } from '@fsv/data';
import { tenantPath } from '../lib/tenantRouting';
import { HelpLink } from '../help/HelpLink';
import { NewsGate } from '../news/NewsGate';
import { WelcomeGate } from '../tour/WelcomeGate';
import { TourProvider } from '../tour/TourProvider';
import { DemoBand } from './DemoBand';
import { UpdateBand } from './UpdateBand';
import { Footer } from './Footer';
import { Icon, type IconName } from './Icon';
import { ClubLogo } from './ClubLogo';
import { TenantSwitcher } from './TenantSwitcher';
import { useToast } from './Toast';
import './AppShell.css';

function subscribeOnline(cb: () => void) {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

/** Time of the newest successful data fetch, for "Offline – Stand 14:32". */
function useLastSync(): Date | null {
  const qc = useQueryClient();
  const [last, setLast] = useState<Date | null>(null);
  useEffect(
    () =>
      qc.getQueryCache().subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'success') setLast(new Date(event.query.state.dataUpdatedAt));
      }),
    [qc],
  );
  return last;
}

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  badge?: number | undefined;
  end?: boolean;
  /** Anchor of the demo tour (tours.ts). */
  tour?: string;
}

/**
 * Frame of every signed-in page. Inside /v/:slug/… it shows that tenant's
 * navigation (role from the active membership) and the tenant switcher;
 * outside (platform area, picker, global profile) only what needs no tenant.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession();
  const tenant = useOptionalTenant();
  const { me } = session;
  const role = tenant?.role ?? null;
  const isEditor = role !== null && hasAtLeastRole(role, 'editor');
  const isAdmin = role === 'admin';
  const inbox = useInboxBadge(tenant, isEditor);
  const online = useOnline();
  const lastSync = useLastSync();
  const toast = useToast();
  const location = useLocation();

  useEffect(() => {
    const onFull = () => toast.show(tx.toast.storageFull, { tone: 'error' });
    window.addEventListener(STORAGE_FULL_EVENT, onFull);
    return () => window.removeEventListener(STORAGE_FULL_EVENT, onFull);
  }, [toast]);

  const platform: NavItem[] = me.isPlatformManager ? [{ to: '/plattform', label: tx.platform.nav, icon: 'platform' }] : [];
  const items: NavItem[] = tenant
    ? [
        { to: tenantPath(tenant.slug), label: de.nav.fleet, icon: 'plane', end: true },
        { to: tenantPath(tenant.slug, '/suche'), label: de.nav.search, icon: 'search' },
        ...(isEditor ? [{ to: tenantPath(tenant.slug, '/eingang'), label: de.nav.inbox, icon: 'inbox' as const, badge: inbox.data?.length, tour: 'nav-inbox' }] : []),
        ...(isAdmin ? [{ to: tenantPath(tenant.slug, '/admin'), label: de.nav.admin, icon: 'admin' as const, tour: 'nav-admin' }] : []),
        ...platform,
        { to: tenantPath(tenant.slug, '/profil'), label: de.nav.profile, icon: 'user' },
      ]
    : [
        // Always the picker, even with one club: "Alle Vereine" that silently opens one club felt wrong (owner, 05.10.2026).
        ...(me.memberships.length ? [{ to: '/vereine', label: tx.tenant.allClubs, icon: 'club' as const }] : []),
        ...platform,
        { to: '/profil', label: de.nav.profile, icon: 'user' },
      ];
  const isPlan = /^\/v\/[^/]+\/flugzeug\//.test(location.pathname);
  const profilePath = tenant ? tenantPath(tenant.slug, '/profil') : '/profil';

  // The tour (demo only) and the "Neuigkeiten" belong to every signed-in page.
  return (
    <TourProvider>
      <div className={`shell${isPlan ? ' shell--plan' : ''}${session.mode === 'demo' ? ' shell--demo' : ''}`}>
        <a className="skip-link" href="#main">
          {tx.app.skipToContent}
        </a>
        <nav className="rail" aria-label="Hauptnavigation">
          <div className="rail__brand" aria-hidden="true">
            {/* The club's logo where it has one (0019), else the app icon. */}
            <ClubLogo club={tenant ?? {}} size={40} fallback={<img className="rail__icon" src="/icons/icon-192.png" alt="" width={40} height={40} />} />
            {/* The active club's code (as in its damage numbers); outside a club the neutral product mark. */}
            <span>{tenant?.code || de.app.mark}</span>
          </div>
          <NavList items={items} variant="rail" />
        </nav>
        <div className="shell__col">
          <UpdateBand />
          <DemoBand />
          <header className="topbar">
            <span className={`topbar__brand${tenant ? ' topbar__brand--tenant' : ''}`}>
              <img className="topbar__mark" src="/icons/icon-192.png" alt="" width={30} height={30} />
              <span className="topbar__name">{de.app.name}</span>
            </span>
            {tenant && <TenantSwitcher current={tenant} memberships={me.memberships} />}
            <span className="spacer" />
            {!online && (
              <span className="pill pill--offline" role="status">
                <Icon name="offline" size={16} />
                {tx.app.offline(lastSync ? formatTime(lastSync) : null)}
              </span>
            )}
            {session.mode === 'demo' && (
              <NavLink to={profilePath} className="pill pill--demo" title={tx.app.demoBanner}>
                {tx.app.demoBadge} · {session.me.displayName.split(' ')[0]}
              </NavLink>
            )}
            <HelpLink tour="help" />
          </header>
          <main id="main" className="shell__main" tabIndex={-1}>
            {children}
          </main>
          {!isPlan && <Footer />}
        </div>
        <nav className="tabbar" aria-label="Hauptnavigation">
          <NavList items={items} variant="tabbar" />
        </nav>
        <WelcomeGate />
        <NewsGate />
      </div>
    </TourProvider>
  );
}

function NavList({ items, variant }: { items: NavItem[]; variant: 'rail' | 'tabbar' }) {
  return (
    <ul className={`navlist navlist--${variant}`}>
      {items.map((item) => (
        <li key={item.to}>
          <NavLink to={item.to} end={item.end ?? false} className="navlist__link" data-tour={item.tour}>
            <span className="navlist__icon">
              <Icon name={item.icon} size={24} />
              {item.badge ? (
                <span className="navlist__badge mono" aria-label={`${item.badge} offen`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </span>
            <span className="navlist__label">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
